/*
 * Felder eines Teilschemas den Datenpunkten einer Anlage zuordnen.
 *
 * Belegt wird nur, was eindeutig ist, in dieser Reihenfolge:
 *   1. Climatix-Adresse (readOa) steht als Symbol oder als Attribut am Punkt.
 *      Das ist der Beleg: dieselbe Adresse liest am Regler denselben Wert.
 *   2. Der Name am Regler (`name`, etwa "Puffer Oben-Istwert") ist der
 *      Anzeigename des Punkts. So heissen die Punkte der Vorlage
 *      "Bösch S400 V1", die aus denselben Siemens-Blaettern erzeugt ist.
 * Passen auf eine Stufe mehrere Punkte, bleibt das Feld frei: lieber eine
 * Luecke, die der Autor sieht, als ein falscher Wert, der echt aussieht.
 *
 * Frei von Laufzeit-Importen, damit der Node-Testlauf die Datei laden kann.
 */

import type { S400Field } from './types';

export interface AutoBindLeaf {
  /** Fertiger Binding-Key des Punkts. */
  key: string;
  symbol: string;
  displayName: string | null;
  attributes: Record<string, string> | null;
}

export function normaliseName(s: string): string {
  return s
    .toLowerCase()
    .replace(/­/g, '')
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '');
}

/**
 * Vorschlag je Feld (Kennung -> Binding-Key). Bereits gebundene Felder
 * (`existing`) bleiben unberuehrt und kommen im Ergebnis nicht vor.
 */
export function autoBindFields(
  fields: readonly S400Field[],
  leaves: readonly AutoBindLeaf[],
  existing: Readonly<Record<string, string>> = {},
): Record<string, string> {
  const byOa = new Map<string, AutoBindLeaf[]>();
  const byName = new Map<string, AutoBindLeaf[]>();
  const push = (m: Map<string, AutoBindLeaf[]>, k: string, l: AutoBindLeaf) => {
    if (!k) return;
    const list = m.get(k);
    if (list) { if (!list.includes(l)) list.push(l); } else m.set(k, [l]);
  };
  for (const leaf of leaves) {
    push(byOa, leaf.symbol.trim(), leaf);
    for (const v of Object.values(leaf.attributes ?? {})) push(byOa, String(v).trim(), leaf);
    if (leaf.displayName) push(byName, normaliseName(leaf.displayName), leaf);
  }

  const out: Record<string, string> = {};
  for (const f of fields) {
    if (existing[f.id]) continue;
    const viaOa = f.readOa ? byOa.get(f.readOa) : undefined;
    if (viaOa?.length === 1) { out[f.id] = viaOa[0]!.key; continue; }
    if (viaOa && viaOa.length > 1) continue;
    const viaName = f.name ? byName.get(normaliseName(f.name)) : undefined;
    if (viaName?.length === 1) out[f.id] = viaName[0]!.key;
  }
  return out;
}
